import React from 'react';
import { useAuth } from '../auth/AuthContext';

/**
 * Gate for an admin route the sidebar/tab strip already hides for most
 * viewers -- this is what a pasted or bookmarked URL hits instead.
 *
 * Like RequireAuth, this is a UX affordance, not a security boundary: the
 * backend's own guard on every endpoint underneath is the real authority,
 * and already returns an accurate 403 if this component were skipped
 * entirely. Rendering a plain explanation here instead of the page shell
 * avoids that page's own fetch-and-403-banner dance, and avoids a redirect
 * (which would be disorienting for a genuine deep link and risks a loop with
 * the "*" catch-all).
 *
 * `resource` must use the SAME "any action" rule the tab strip uses
 * (adminTabs.js::visibleAdminTabs), or a route would be blocked here for
 * someone the tab correctly shows it to.
 *
 * DELIBERATELY STILL ON can(), and not on the per-record flags issue #181
 * added. This asks a ROUTE-level question -- "is this whole screen worth
 * showing" -- which has no record to be about, and for which "holds this
 * somewhere" is precisely the right answer: a node-scoped hierarchy admin
 * should reach /admin/hierarchy and then find only their own branch
 * actionable, which is what the per-record flags inside the page now deliver.
 * Narrowing this gate instead would lock them out of the screen entirely.
 */
function RequirePermission({ resource, children }) {
  const { can } = useAuth();
  const allowed = (
    can(resource, 'read') || can(resource, 'create')
    || can(resource, 'update') || can(resource, 'delete')
  );

  if (!allowed) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>No access</h3>
          <p>You don't have permission to view this area.</p>
        </div>
      </div>
    );
  }

  return children;
}

export default RequirePermission;
